// F7: client for /me/notifications*, /me/notification-prefs, /orgs/{id}/integrations* and
// /orgs/{id}/webhooks* (server/src/{notifications,destinations,org_webhooks}.rs).
import { api } from "../api/client";

export type NotificationKind =
  | "recording.ready"
  | "recording.shared_with_you"
  | "meeting.notes_ready"
  | "transcript.ready"
  | "share_link.view_limit_reached"
  | "org.invite"
  | "recording.retention_scheduled"
  | "quota.threshold";

export type NotificationChannel = "in_app" | "email" | "desktop";

export interface NotificationOut {
  id: string;
  kind: NotificationKind | string;
  org_id?: string | null;
  /** Display-only: title / link / names. Never transcript text (FR-M7). */
  payload: Record<string, unknown>;
  created_at: string;
  read_at: string | null;
  /** Set only when the recording is the reader's own and is no longer live: "trashed" can be
   * restored from that org's trash, "deleted" is gone. Never set for anyone else's recording
   * (§4.10). */
  recording?: { state: "trashed" | "deleted"; org_slug: string };
}

export interface NotificationList {
  items: NotificationOut[];
  next_cursor: string | null;
  unread_count: number;
}

export interface NotificationPref {
  kind: NotificationKind | string;
  channel: NotificationChannel;
  enabled: boolean;
  /** False where the channel can't deliver this kind at all (email for every kind but shares
   * and invites, which have no email template). Absent from older servers: treat as true. */
  available?: boolean;
}

export function listNotifications(cursor?: string): Promise<NotificationList> {
  const qs = cursor ? `?cursor=${encodeURIComponent(cursor)}` : "";
  return api("GET", `/me/notifications${qs}`);
}

export function markNotificationRead(id: string): Promise<void> {
  return api("POST", `/me/notifications/${id}/read`);
}

export function markAllNotificationsRead(): Promise<void> {
  return api("POST", "/me/notifications/read-all");
}

export function getNotificationPrefs(): Promise<{ items: NotificationPref[] }> {
  return api("GET", "/me/notification-prefs");
}

export function putNotificationPrefs(prefs: NotificationPref[]): Promise<{ items: NotificationPref[] }> {
  return api("PUT", "/me/notification-prefs", { prefs });
}

// ---- Destinations (admin) --------------------------------------------------------------

export type DestinationChannel = "cipher" | "slack" | "teams";

export interface DestinationState {
  configured: boolean;
  /** `…last4` — the only form of the URL the API ever returns. */
  masked: string | null;
  enabled: boolean;
}

export interface IntegrationsOut {
  cipher: DestinationState;
  slack: DestinationState;
  teams: DestinationState;
  include_notes_text: boolean;
}

/** `PUT` is partial: an absent key keeps the stored value, `null` clears a URL. The API never
 * returns a stored URL, so a client can only ever send the one it is changing. */
export interface IntegrationsIn {
  cipher_url?: string | null;
  cipher_enabled?: boolean;
  slack_url?: string | null;
  teams_url?: string | null;
  include_notes_text?: boolean;
}

export function getIntegrations(orgId: string): Promise<IntegrationsOut> {
  return api("GET", `/orgs/${orgId}/integrations`);
}

export function putIntegrations(orgId: string, body: IntegrationsIn): Promise<IntegrationsOut> {
  return api("PUT", `/orgs/${orgId}/integrations`, body);
}

export function testIntegration(orgId: string, channel: DestinationChannel): Promise<{ ok: boolean; status_code: number }> {
  return api("POST", `/orgs/${orgId}/integrations/${channel}/test`);
}

// ---- Outbound webhooks (admin) ---------------------------------------------------------

export const WEBHOOK_EVENTS = [
  "recording.published",
  "recording.trashed",
  "recording.restored",
  "recording.shared",
  "share_link.created",
  "share_link.revoked",
  "transcript.ready",
  "meeting.notes_ready",
  "member.joined",
  "member.removed",
] as const;

export interface WebhookOut {
  id: string;
  url_masked: string;
  events: string[];
  active: boolean;
  consecutive_failures: number;
  disabled_at: string | null;
  created_at: string;
}

export interface WebhookCreated extends Omit<WebhookOut, "consecutive_failures" | "disabled_at"> {
  /** Shown exactly once — never returned again by any endpoint. */
  secret: string;
}

export interface WebhookDelivery {
  id: string;
  event_type: string;
  status: string;
  attempts: number;
  response_code: number | null;
  duration_ms: number | null;
  last_error: string | null;
  created_at: string;
  delivered_at: string | null;
}

export function listWebhooks(orgId: string): Promise<{ items: WebhookOut[] }> {
  return api("GET", `/orgs/${orgId}/webhooks`);
}

export function createWebhook(orgId: string, body: { url: string; events: string[] }): Promise<WebhookCreated> {
  return api("POST", `/orgs/${orgId}/webhooks`, body);
}

export function updateWebhook(orgId: string, id: string, body: { active?: boolean; events?: string[]; url?: string }): Promise<WebhookOut> {
  return api("PATCH", `/orgs/${orgId}/webhooks/${id}`, body);
}

export function deleteWebhook(orgId: string, id: string): Promise<void> {
  return api("DELETE", `/orgs/${orgId}/webhooks/${id}`);
}

export function listWebhookDeliveries(
  orgId: string,
  id: string,
  cursor?: string,
): Promise<{ items: WebhookDelivery[]; next_cursor: string | null }> {
  const qs = cursor ? `?cursor=${encodeURIComponent(cursor)}` : "";
  return api("GET", `/orgs/${orgId}/webhooks/${id}/deliveries${qs}`);
}

export function redeliverWebhook(orgId: string, id: string, deliveryId: string): Promise<void> {
  return api("POST", `/orgs/${orgId}/webhooks/${id}/deliveries/${deliveryId}/redeliver`);
}
